import { Controller } from "@hotwired/stimulus"

export default class FillAddressController extends Controller {
  static targets = ["country", "state", "city", 'postalCode']

  async connect() {
    this.initialCountry = this.countryTarget.getAttribute("value") || ""
    this.initialState = this.stateTarget.getAttribute("value") || ""
    this.initialCity = this.cityTarget.getAttribute("value") || ""

    try {
      this.Country = (await import("country-state-city/lib/country.js")).default
      this.loadCountries()
      await this.restoreInitialSelection()
    } catch (error) {
      console.error("Unable to load country data", error)
    }
  }

  loadCountries() {
    const countryTarget = this.countryTarget
    const existingCodes = new Set(
      Array.from(countryTarget.options, option => option.value)
    )
    const options = document.createDocumentFragment()

    this.Country.getAllCountries().forEach(country => {
      if (!existingCodes.has(country.isoCode)) {
        options.append(new Option(country.name, country.isoCode))
      }
    })

    countryTarget.append(options)
  }

  async restoreInitialSelection() {
    if (!this.initialCountry) return

    this.restoringSelection = true

    try {
      this.selectSavedValue(this.countryTarget, this.initialCountry)
      await this.countryChanged()

      this.selectSavedValue(this.stateTarget, this.initialState)
      await this.stateChanged()

      this.selectSavedValue(this.cityTarget, this.initialCity)
    } finally {
      this.restoringSelection = false
    }
  }

  selectSavedValue(target, savedValue) {
    if (!savedValue) return

    const option = Array.from(target.options).find(item =>
      item.value === savedValue || item.text === savedValue
    )

    if (option) target.value = option.value
  }

  async countryChanged(event) {
    const countryCode = this.countryTarget.value

    this.stateTarget.innerHTML = '<option value="">Select State</option>'
    this.cityTarget.innerHTML = '<option value="">Select City</option>'

    if (!countryCode) return

    try {
      this.State ||= (await import("country-state-city/lib/state.js")).default
    } catch (error) {
      console.error("Unable to load state data", error)
      return
    }

    if (this.countryTarget.value !== countryCode) return

    this.State.getStatesOfCountry(countryCode).forEach(state => {
      this.stateTarget.add(
        new Option(state.name, state.isoCode)
      )
    })
    if (!this.restoringSelection) this.postalCodeChange(event)
  }

  async stateChanged(event) {
    const countryCode = this.countryTarget.value
    const stateCode = this.stateTarget.value

    this.cityTarget.innerHTML = '<option value="">Select City</option>'

    if (!countryCode || !stateCode) return

    this.cityRequest?.abort()
    const cityRequest = new AbortController()
    this.cityRequest = cityRequest
    this.cityTarget.disabled = true

    try {
      const query = new URLSearchParams({ country_code: countryCode, state_code: stateCode })
      const response = await fetch(`/locations/cities?${query}`, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
        signal: cityRequest.signal
      })

      if (!response.ok) throw new Error(`City request failed with status ${response.status}`)

      const cities = await response.json()

      if (this.countryTarget.value !== countryCode ||
        this.stateTarget.value !== stateCode) return

      const options = document.createDocumentFragment()
      cities.forEach(city => options.append(new Option(city, city)))
      this.cityTarget.append(options)
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Unable to load city data", error)
      }
    } finally {
      if (this.cityRequest === cityRequest &&
        this.countryTarget.value === countryCode &&
        this.stateTarget.value === stateCode) {
        this.cityTarget.disabled = false
      }
    }

    if (!this.restoringSelection) this.postalCodeChange(event)
  }

  disconnect() {
    this.cityRequest?.abort()
    clearTimeout(this.postalValidationTimer)
    this.postalValidationRequest = (this.postalValidationRequest || 0) + 1
  }

  cityChanged(event) {
    this.postalCodeChange(event)
  }

  postalCodeChange(event) {
    if (event.data === " " || !this.countryTarget.value) return;
    const postalCode = this.postalCodeTarget.value.trim()
    const requestId = (this.postalValidationRequest || 0) + 1
    this.postalValidationRequest = requestId
    clearTimeout(this.postalValidationTimer)

    if (!postalCode) {
      delete this.postalCodeTarget.dataset.invalid
      delete this.postalCodeTarget.dataset.validating
      delete this.postalCodeTarget.dataset.verificationError
      return
    }

    this.postalCodeTarget.dataset.invalid = "true"
    this.postalCodeTarget.dataset.validating = "true"
    delete this.postalCodeTarget.dataset.verificationError
    this.refreshPostalValidationMessage()

    this.postalValidationTimer = setTimeout(() => {
      this.verifyPostalCode(postalCode, requestId)
    }, 300)
  }

  async verifyPostalCode(postalCode, requestId) {
    const mapsReady = await this.waitForGoogleMaps()
    if (!mapsReady || requestId !== this.postalValidationRequest) return

    try {
      const { results, status } = await this.geocodeWithTimeout(postalCode)

      if (requestId !== this.postalValidationRequest ||
        this.postalCodeTarget.value.trim() !== postalCode) return

      let isValid = status === "OK" && results.length > 0

      if (isValid) {
        isValid = this.locationMatches(results[0].address_components)
      }

      this.postalCodeTarget.dataset.invalid = String(!isValid)
      this.postalCodeTarget.dataset.validating = "false"
      delete this.postalCodeTarget.dataset.verificationError
      this.refreshPostalValidationMessage()
    } catch (error) {
      if (requestId !== this.postalValidationRequest) return

      this.postalCodeTarget.dataset.invalid = "true"
      this.postalCodeTarget.dataset.validating = "false"
      this.postalCodeTarget.dataset.verificationError = "true"
      this.refreshPostalValidationMessage()
      console.error("Unable to verify postal code", error)
    }
  }

  geocodeWithTimeout(postalCode) {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error("Postal code verification timed out"))
      }, 2500)
      const geocoder = new globalThis.google.maps.Geocoder()

      geocoder.geocode(
        {
          address: postalCode,
          componentRestrictions: {
            country: this.countryTarget.value
          }
        },
        (results, status) => {
          clearTimeout(timeout)
          resolve({ results: results || [], status })
        }
      )
    })
  }

  locationMatches(components) {
    const componentValues = (...types) => components
      .filter(component => types.some(type => component.types.includes(type)))
      .flatMap(component => [component.long_name, component.short_name])
      .map(value => this.normalizeLocation(value))

    const cityValues = componentValues(
      "locality",
      "postal_town",
      "sublocality",
      "administrative_area_level_2",
      "administrative_area_level_3"
    )
    const stateValues = componentValues("administrative_area_level_1")
    const countryValues = componentValues("country")

    return this.selectedLocationMatches(this.cityTarget, cityValues) &&
      this.selectedLocationMatches(this.stateTarget, stateValues) &&
      this.selectedLocationMatches(this.countryTarget, countryValues)
  }

  selectedLocationMatches(target, validValues) {
    if (!target.value) return true

    const selected = target.selectedOptions[0]
    const selectedValues = [target.value, selected?.text]
      .map(value => this.normalizeLocation(value))

    return selectedValues.some(value => validValues.includes(value))
  }

  normalizeLocation(value) {
    return String(value || "").trim().toLocaleLowerCase()
  }

  async waitForGoogleMaps() {
    for (let attempt = 0; attempt < 10; attempt += 1) {
      if (globalThis.google?.maps?.Geocoder) return true

      await new Promise(resolve => setTimeout(resolve, 100))
    }

    this.postalCodeTarget.dataset.invalid = "true"
    this.postalCodeTarget.dataset.validating = "false"
    this.postalCodeTarget.dataset.verificationError = "true"
    this.refreshPostalValidationMessage()
    return false
  }

  refreshPostalValidationMessage() {
    const input = $(this.postalCodeTarget)
    if (input.closest("form").data("validator")) input.valid()
  }
}
